import { gql } from 'apollo-boost';

import { ValidationErrorFragment, ValidationWarningFragment } from '../../mutations/grid/common';

import { commonBidModifiersAdjustmentFragment } from './bidModifiers';
import { vCardCampaignDataFragment, vcardConstantsFragment } from './common';
import { allowedBidModifiersValuesFragment } from './rateCorrections';
import { recommendationKeyFragment } from './recommendations';

export const priceCampaignDataForGroupFragment = gql`
    fragment PriceCampaignDataForGroupFragment on GdPriceCampaign {
        startDate
        endDate
        flightOrderVolume
        pricePackageId
        flightTargetingsSnapshot {
            behaviors
            viewTypes
            geoExpanded
        }
        pricePackage {
            id
            title
            targetingsFixed {
                geo
            }
            targetingsCustom {
                geo
            }
        }
    }
`;

export const clientFeaturesFragment = gql`
    fragment ClientFeaturesFragment on GdClientFeatures {
        isWalletEnabled
        hasMetrikaCounters
        hasEcommerce
        hasCampaignsWithCPAStrategy
        enabledRecommendationTypes
        isBannerPricesOnGridEnabled
        isInternalAdsAllowed
        isCpmCampaignsEnabled
        isTurboForSerpWizardAllowed
        isBrandLiftAllowed
        isTextBannerInterestsRetCondEnabled
        isTycoonOrganizationsEnabled
        isCampaignDefaultOrganizationsEnabled
        isShowAutoAssignedOrganizationsEnabled
        isInAppEventsInRmpEnabled
        isTargetTagsAllowed
    }
`;

export const goalStatsGridFragment = gql`
    fragment GoalStatsGridFragment on GdGoalStats {
        conversionRate
        costPerAction
        goalId
        goals
    }
`;

export const campaignBrandSurveyFragment = gql`
    fragment campaignBrandSurveyFragment on GdBrandSurveyStatus {
        surveyStatus
        reasonIds
        brandSurveyStopReasons
    }
`;

export const pokazometerDataFragment = gql`
    fragment PokazometerDataFragment on GdPokazometerData {
        allCostsAndClicks {
            clicks
            price
        }
        priceForCoverage {
            coverage
            price
        }
        maxShows
    }
`;

export const campaignStrategyBudgetFragment = gql`
    fragment campaignStrategyBudgetFragment on GdCampaignBudget {
        sum
        showMode
        period
        start
        finish
    }
`;

export const campaignFlatStrategyFragment = gql`
    fragment campaignFlatStrategyFragment on GdCampaignFlatStrategy {
        strategyType
        isAutoBudget
        platform
        budget {
            ...campaignStrategyBudgetFragment
        }
        ... on GdCampaignStrategyManual {
            separateBidding
        }

        ... on GdStrategyOptimizeConversions {
            payForConversion
        }
    }

    ${campaignStrategyBudgetFragment}
`;

export const recommendationGraphFragment = gql`
    fragment recommendationGraphFragment on GdRecommendationKpiGraph {
        typeY
        typeX
        points {
            valueX
            typeValueX
            profitValue
            currentValue
            holiday
        }
    }
`;

export const recommendationKpiFragment = gql`
    fragment recommendationKpiFragment on GdRecommendationKpi {
        sign
        clicks
        cost
        period
        currency
        graph {
            ...recommendationGraphFragment
        }
    }

    ${recommendationGraphFragment}
`;

export const recommendationFragment = gql`
    fragment recommendationFragment on GdRecommendation {
        type
        status
        isApplicable
        keys {
            ...recommendationKeyFragment
        }
        ... on GdRecommendationWithKpi {
            kpi {
                ...recommendationKpiFragment
                ... on GdRecommendationKpiRemovePagesFromBlackListOfACampaign {
                    pageNames
                }
                ... on GdRecommendationKpiDailyBudget {
                    recommendedDailyBudget
                }
                ... on GdRecommendationKpiAddBannerFormats {
                    formats
                }
                ... on GdRecommendationKpiVideoFormats {
                    videoFormats {
                        width
                        height
                        duration
                    }
                }
                ... on GdRecommendationKpiChooseAppropriatePlacementsForAdGroup {
                    ratioInfoList {
                        creativesCount
                        videoFormat {
                            width
                            height
                            duration
                        }
                    }
                }
            }
        }
    }
    ${recommendationKpiFragment}
    ${recommendationKeyFragment}
`;

export const timeTargetStatusInfoFragment = gql`
    fragment timeTargetStatusInfoFragment on TimeTargetStatusInfo {
        activationTime
        status
    }
`;

export const campaignStatusInfoFragment = gql`
    fragment campaignStatusInfoFragment on GdCampaignStatus {
        over
        draft
        activating
        primaryStatus
        primaryStatusDesc
        needsNewPayment
        moneyBlocked
        timeTargetStatus {
            ...timeTargetStatusInfoFragment
        }
    }
    ${timeTargetStatusInfoFragment}
`;

export const statsDataFragment = gql`
    fragment statsDataFragment on GdEntityStats {
        ctr
        cpmPrice
        profitability
        cost
        avgClickPosition
        conversionRate
        bounceRate
        revenue
        shows
        avgClickCost
        avgShowPosition
        avgDepth
        clicks
        avgGoalCost
        goals
    }
`;

export const campaignRowCpmFragment = gql`
    fragment campaignRowCpmFragment on GdCpmBannerCampaign {
        brandSurveyId
        brandSurveyStatus {
            ...campaignBrandSurveyFragment
        }
    }
    ${campaignBrandSurveyFragment}
`;

export const campaignRowFragment = gql`
    fragment campaignsRowFragment on GdCampaign {
        id
        agencyId
        name
        index
        currency
        ...campaignRowCpmFragment
        type
        sum
        hasAds
        startDate
        endDate
        recommendations {
            ...recommendationFragment
        }
        strategy {
            ...campaignFlatStrategyFragment
        }
        aggregatedStatus
        aggregatedStatusInfo {
            status
            reasons
            counters {
                adgroups
            }
        }
        status {
            ...campaignStatusInfoFragment
        }
        access {
            canEdit
            actions
        }
        stats {
            ...statsDataFragment
        }
        goalStats {
            ...GoalStatsGridFragment
        }
    }
    ${campaignRowCpmFragment}
    ${campaignFlatStrategyFragment}
    ${statsDataFragment}
    ${campaignStatusInfoFragment}
    ${recommendationFragment}
    ${goalStatsGridFragment}
`;

export const campaignShortRowFragment = gql`
    fragment campaignShortRowFragment on GdCampaign {
        id
        name
        type
        currency
        status {
            ...campaignStatusInfoFragment
        }
        access {
            canEdit
            actions
        }
        strategy {
            ...campaignFlatStrategyFragment
        }
    }
    ${campaignStatusInfoFragment}
    ${campaignFlatStrategyFragment}
`;

export const campaignTruncatedFragment = gql`
    fragment CampaignTruncatedFragment on GdCampaignTruncated {
        id
        isAimingAllowed
        name
        type
        currency
        status {
            ...campaignStatusInfoFragment
        }
        access {
            canEdit
            actions
        }
        strategy {
            ...campaignFlatStrategyFragment
        }
        ... on GdCampaign {
            hasAds
        }
        ... on GdTextCampaign {
            lastChangedVcardId
            commonVcard {
                ...VCardCampaignDataFragment
            }
        }
        ... on GdDynamicCampaign {
            lastChangedVcardId
            commonVcard {
                ...VCardCampaignDataFragment
            }
        }
    }
    ${campaignStatusInfoFragment}
    ${vCardCampaignDataFragment}
    ${campaignFlatStrategyFragment}
`;

export const internalFreeCampaignTruncatedFragment = gql`
    fragment InternalFreeCampaignTruncatedFragment on GdInternalFreeCampaignTruncated {
        id
        isAimingAllowed
        name
        type
        currency
        placeId
        status {
            ...campaignStatusInfoFragment
        }
        adsCount
        access {
            canEdit
            actions
        }
        strategy {
            ...campaignFlatStrategyFragment
        }
    }
    ${campaignStatusInfoFragment}
    ${campaignFlatStrategyFragment}
`;

export const internalDistribCampaignTruncatedFragment = gql`
    fragment InternalDistribCampaignTruncatedFragment on GdInternalDistribCampaignTruncated {
        id
        isAimingAllowed
        name
        type
        currency
        placeId
        adsCount
        status {
            ...campaignStatusInfoFragment
        }
        access {
            canEdit
            actions
        }
        strategy {
            ...campaignFlatStrategyFragment
        }
    }
    ${campaignStatusInfoFragment}
    ${campaignFlatStrategyFragment}
`;

export const adGroupInternalTruncatedFragment = gql`
    fragment AdGroupInternalTruncatedFragment on GdAdGroupTruncated {
        campaign {
            ...InternalFreeCampaignTruncatedFragment
            ...InternalDistribCampaignTruncatedFragment
        }
        access {
            canEdit
        }
        id
        minusKeywords
        name
        regionsInfo {
            regionIds
        }
        status {
            bsEverSynced
            blGenerationStatus
            moderationStatus
            primaryStatus
            primaryStatusDesc
        }
        adsCount
        type
    }

    ${internalFreeCampaignTruncatedFragment}
    ${internalDistribCampaignTruncatedFragment}
`;

export const campaignBaseInfoRowFragment = gql`
    fragment campaignBaseInfoRowFragment on GdCampaign {
        id
        currency
        name
        agencyId
        type
        strategy {
            ...campaignFlatStrategyFragment
        }
        status {
            ...campaignStatusInfoFragment
        }
        access {
            canEdit
            actions
        }
    }
    ${campaignFlatStrategyFragment}
    ${campaignStatusInfoFragment}
`;

export const adsGroupRowFragment = gql`
    fragment adsGroupRowFragment on GdAdGroup {
        name
        type
        campaignId
        id
        aggregatedStatus
        aggregatedStatusInfo {
            status
            reasons
            counters {
                ads
                keywords
            }
        }
        index
        adsCount
        campaign {
            ...CampaignTruncatedFragment
        }
        recommendations {
            ...recommendationFragment
        }
        minusKeywords
        regionsInfo {
            regionIds
        }
        access {
            canBeDeleted
            canEdit
            canRemoderateAdsCallouts
            canAcceptAdsCalloutsModeration
            canBeSentToModeration
            canBeSentToModerationByClient
            canBeSentToRemoderation
            canAcceptModeration
            canBeSentToBS
            canCopy
            canEditRegions
        }
        stats {
            ...statsDataFragment
        }
        ... on GdDynamicAdGroup {
            mainDomain
        }
        ... on GdMobileContentAdGroup {
            storeHref
        }
        ... on GdInternalAdGroup {
            level
            targetingInternalNetwork {
                targetingMode
                joinType
            }
            targetingIsBrowser {
                targetingMode
                joinType
            }
            targetingIsTablet {
                targetingMode
                joinType
            }
            targetingIsMobile {
                targetingMode
                joinType
            }
            targetingIsTouch {
                targetingMode
                joinType
            }
            targetingUserAgents {
                targetingMode
                joinType
                value
            }
            targetingOsNames {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                }
            }
            targetingOsFamilies {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                    minVersion
                    maxVersion
                }
            }
            targetingBrowserNames {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                    minVersion
                    maxVersion
                }
            }
            targetingBrowserEngines {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                    minVersion
                    maxVersion
                }
            }
            targetingDeviceVendors {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                }
            }
            targetingDeviceNames {
                targetingMode
                joinType
                value
            }
            targetingInterfaceLangs {
                targetingMode
                joinType
                value
            }
            targetingQueryReferers {
                targetingMode
                joinType
                value
            }
            targetingYandexUids {
                targetingMode
                joinType
                value
            }
            targetingShowDates {
                targetingMode
                joinType
                value
            }
            targetingClidTypes {
                targetingMode
                joinType
                value
            }
            targetingClids {
                targetingMode
                joinType
                value
            }
            targetingDesktopInstalledApps {
                targetingMode
                joinType
                value
            }
            targetingMobileInstalledApps {
                targetingMode
                joinType
                value
            }
            targetingQueryOptions {
                targetingMode
                joinType
                value
            }
            targetingTestIds {
                targetingMode
                joinType
                value
            }
            targetingYsCookies {
                targetingMode
                joinType
                value
            }
            targetingIsYandexPlus {
                targetingMode
                joinType
            }
            targetingIsVirused {
                targetingMode
                joinType
            }
            startTime
            finishTime
        }
        status {
            blGenerationStatus
            bsEverSynced
            moderationStatus
            primaryStatus
            primaryStatusDesc
        }
        goalStats {
            ...GoalStatsGridFragment
        }
        availableShowConditionTypes
    }
    ${statsDataFragment}
    ${campaignTruncatedFragment}
    ${recommendationFragment}
    ${goalStatsGridFragment}
`;

export const bannerImageSmartCenterFragment = gql`
    fragment adImageSmartCentersFragment on GdImageSmartCenter {
        height
        ratio
        width
        x
        y
    }
`;

export const bannerImageFormatsFragment = gql`
    fragment adImageFormatsFragment on GdImageFormat {
        imageSize {
            height
            width
        }
        path
        smartCenters {
            ...adImageSmartCentersFragment
        }
    }
    ${bannerImageSmartCenterFragment}
`;

export const bannerImageFragment = gql`
    fragment adImageFragment on GdImage {
        mdsGroupId
        namespace
        imageSize {
            height
            width
        }
        name
        imageHash
        formats {
            ...adImageFormatsFragment
        }
    }
    ${bannerImageFormatsFragment}
`;

export const adsPhraseRowFragment = gql`
    fragment adsPhraseRowFragment on GdAd {
        id
        campaignId
        adGroupId
        type
        title
        titleExtension
        body
        access {
            canEdit
        }
        domain
        href
        image {
            ...adImageFragment
        }
        bannerImage {
            ...adImageFragment
        }
        typedCreative {
            previewUrl
            height
            width
            name
            ... on GdCanvasCreative {
                isAdaptive
            }
        }
        status {
            primaryStatus
            previousVersionShown
            rejectedOnModeration
        }
        goalStats {
            ...GoalStatsGridFragment
        }
        recommendations {
            ...recommendationFragment
        }
        stats {
            ...statsDataFragment
        }
        linkTail
        dynamicDisclaimer
        sitelinks {
            href
        }
        flags
        ... on GdContentPromotionAd {
            contentPromotionPreviewUrl
            contentPromotionVisitUrl
            contentPromotionUrl
        }
    }
    ${statsDataFragment}
    ${bannerImageFragment}
    ${recommendationFragment}
    ${goalStatsGridFragment}
`;

export const adPreviewFragment = gql`
    fragment adPreviewFragment on GdAd {
        id
        campaignId
        adGroupId
        type
        title
        titleExtension
        body
        access {
            canEdit
        }
        domain
        href
        image {
            ...adImageFragment
        }
        bannerImage {
            ...adImageFragment
        }
        typedCreative {
            previewUrl
            height
            width
            name
            ... on GdCanvasCreative {
                isAdaptive
            }
        }
        ... on GdContentPromotionAd {
            contentPromotionPreviewUrl
        }
        linkTail
        dynamicDisclaimer
        sitelinks {
            href
        }
        flags
    }
    ${bannerImageFragment}
`;

export const adGroupTruncatedFragment = gql`
    fragment AdGroupTruncatedFragment on GdAdGroupTruncated {
        campaign {
            ...CampaignTruncatedFragment
        }
        ... on GdDynamicAdGroupTruncated {
            mainDomain
        }
        ... on GdMobileContentAdGroupTruncated {
            storeHref
        }
        ... on GdSmartAdGroupTruncated {
            feedId
        }
        access {
            canEdit
        }
        mainAd {
            ...adsPhraseRowFragment
        }
        id
        minusKeywords
        name
        regionsInfo {
            regionIds
        }
        status {
            bsEverSynced
            blGenerationStatus
            moderationStatus
            primaryStatus
            primaryStatusDesc
        }
        adsCount
        type
    }
    ${adsPhraseRowFragment}
    ${campaignTruncatedFragment}
`;

export const adsRowFragment = gql`
    fragment adsRowFragment on GdAd {
        id
        campaignId
        adGroupId
        isAimingAllowed
        exportId
        type
        title
        titleExtension
        hasCallouts
        hasSitelink
        hasTurboGallery
        hasTurbolanding
        hasVideo
        adGroup {
            ...AdGroupTruncatedFragment
        }
        body
        access {
            canEdit
        }
        domain
        href
        turbolanding {
            href
        }
        image {
            ...adImageFragment
        }
        bannerImage {
            ...adImageFragment
        }
        typedCreative {
            creativeId
            previewUrl
            height
            width
            name
            ... on GdSmartCreative {
                layout {
                    name
                }
            }
            ... on GdCpcVideoCreative {
                livePreviewUrl
            }
            ... on GdVideoAdditionCreative {
                livePreviewUrl
            }
            ... on GdCpmVideoCreative {
                livePreviewUrl
            }
            ... on GdCpmAudioCreative {
                livePreviewUrl
                duration
            }
            ... on GdCpmOutdoorCreative {
                livePreviewUrl
            }
            ... on GdCpmIndoorCreative {
                livePreviewUrl
            }
            ... on GdCanvasCreative {
                isAdaptive
            }
        }
        aggregatedStatus
        aggregatedStatusInfo {
            status
            reasons
        }
        status {
            primaryStatus
            previousVersionShown
            rejectedOnModeration
            placementPagesRequired
            hasInactiveResources
        }
        recommendations {
            ...recommendationFragment
        }
        stats {
            ...statsDataFragment
        }
        goalStats {
            ...GoalStatsGridFragment
        }
        linkTail
        dynamicDisclaimer
        sitelinks {
            href
        }
        flags
        ... on GdTextAd {
            turboGalleryHref
            bannerPrice {
                currency
                prefix
                price
                priceOld
            }
        }
        ... on GdInternalAd {
            description
        }
        ... on GdCPMOutdoorBannerAd {
            placementPages {
                bannerId
                comment
                createTime
                id
                pageId
                statusModerate
                statusModerateOperator
                version
                __typename
            }
            __typename
        }
        ... on GdContentPromotionAd {
            contentPromotionId
            contentPromotionUrl
            contentPromotionType
            contentPromotionVisitUrl
            contentPromotionPreviewUrl
            __typename
        }
    }
    ${statsDataFragment}
    ${bannerImageFragment}
    ${adGroupTruncatedFragment}
    ${recommendationFragment}
    ${goalStatsGridFragment}
`;

export const showConditionFragment = gql`
    fragment showConditionsRowFragment on GdShowCondition {
        id
        aggregatedStatus
        aggregatedStatusInfo {
            status
            reasons
        }
        status {
            suspended
            primaryStatus
        }
        adGroup {
            ...AdGroupTruncatedFragment
        }
        access {
            canEdit
        }
        adsInAdGroupCount
        mainAd {
            ...adsPhraseRowFragment
        }
        campaignId
        adGroupId
        index
        id
        type
        ... on GdKeyword {
            priceContext
            weightedCtr
            autobudgetPriority
            showsForecast
            price
            usePhraseTrafaretAuction
            minusKeywords
            pokazometerData {
                ...PokazometerDataFragment
            }
            keyword
            auctionData {
                auctionDataItems {
                    bid
                    amnestyPrice
                    trafficVolume
                    showInTable
                }
            }
        }
        ... on GdRelevanceMatch {
            autobudgetPriority
            price
            priceContext
        }
        stats {
            ...statsDataFragment
        }
        goalStats {
            ...GoalStatsGridFragment
        }
    }
    ${pokazometerDataFragment}
    ${statsDataFragment}
    ${adsPhraseRowFragment}
    ${adGroupTruncatedFragment}
    ${goalStatsGridFragment}
`;

export const autotargetingsRowFragment = gql`
    fragment autotargetingsRowFragment on GdShowCondition {
        id
        adGroupId
        ... on GdRelevanceMatch {
            price
            autobudgetPriority
        }
        adGroup {
            ...AdGroupTruncatedFragment
        }
        status {
            primaryStatus
            deleted
            suspended
            statusModerate
        }
        access {
            canEdit
        }
        stats {
            ...statsDataFragment
        }
    }
    ${adGroupTruncatedFragment}
`;

export const campaignsQuery = gql`
    query CampaignsQuery($login: String!, $campaignInput: GdCampaignsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
                countryRegionId
            }
            campaigns(input: $campaignInput) {
                rowset {
                    ...campaignsRowFragment
                }
                totalCount
                campaignIds
                cacheKey
                totalStats {
                    cost
                    shows
                    clicks
                    ctr
                }
            }
        }
    }
    ${campaignRowFragment}
    ${clientFeaturesFragment}
`;

export const campaignPayActionInfoQuery = gql`
    query CampaignPayActionInfoQuery($login: String!, $campaignInput: GdCampaignsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            campaigns(input: $campaignInput) {
                features {
                    campaignTypes
                }
                rowset {
                    id
                    name
                    access {
                        actions
                    }
                    status {
                        moderationStatus
                    }
                }
            }
        }
    }
`;

export const groupsQuery = gql`
    query GroupsQuery($login: String!, $adGroupInput: GdAdGroupsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
                countryRegionId
            }
            adGroups(input: $adGroupInput) {
                rowset {
                    ...adsGroupRowFragment
                }
                features {
                    hasAdGroupsOverLimit
                }
                totalCount
                adGroupIds
                cacheKey
                totalStats {
                    shows
                    clicks
                    ctr
                    cost
                }
            }
        }
    }
    ${adsGroupRowFragment}
    ${clientFeaturesFragment}
`;

export const groupsMassActionsCount = gql`
    query GroupsMassActionsCount($login: String!, $adGroupInput: GdAdGroupsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            adGroups(input: $adGroupInput) {
                features {
                    canRemoderateAdsCalloutsCount
                    canAcceptAdsCalloutsModerationCount
                    canBeDeletedAdGroupsCount
                    canBeSentToModerationAdGroupsCount
                    canBeSentToBSAdGroupsCount
                    canBeSentToRemoderationAdGroupsCount
                    canAcceptModerationAdGroupsCount
                    canBeSentToModerationByClientAdGroupsCount
                    canBeCopied
                    canBeEditedRegionsCount
                }
            }
        }
    }
`;

export const phrasesQuery = gql`
    query PhrasesQuery($showConditionsInput: GdShowConditionsContainerInput!, $login: String) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
                countryRegionId
            }
            showConditions(input: $showConditionsInput) {
                rowset {
                    ...showConditionsRowFragment
                }
                totalCount
                showConditionIds
                features {
                    commonPlatform
                    commonCampaignTypes
                }
                totalStats {
                    shows
                    clicks
                    ctr
                    cost
                }
                hasObjectsOverLimit
                cacheKey
            }
        }
    }
    ${showConditionFragment}
    ${clientFeaturesFragment}
`;

export const autotargetingsQuery = gql`
    query TargetingQuery($login: String!, $showConditionsInput: GdShowConditionsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
                countryRegionId
            }
            showConditions(input: $showConditionsInput) {
                rowset {
                    ...autotargetingsRowFragment
                }
                cacheKey
                totalCount
                showConditionIds
                hasObjectsOverLimit
            }
        }
    }
    ${adGroupTruncatedFragment}
    ${autotargetingsRowFragment}
    ${statsDataFragment}
    ${clientFeaturesFragment}
`;

export const retargetingsRowFragment = gql`
    fragment retargetingsRowFragment on GdRetargeting {
        access {
            canEdit
        }
        retargetingId
        isSuspended
        priceContext
        retargetingCondition {
            name
            description
            conditionRules {
                type
                interestType
                goals {
                    id
                    type
                    time
                }
            }
        }
        adGroup {
            ...AdGroupTruncatedFragment
        }
        stats {
            ...statsDataFragment
        }
    }
    ${adGroupTruncatedFragment}
    ${statsDataFragment}
`;

export const retargetingsQuery = gql`
    query RetargetingsQuery(
        $login: String!
        $retargetingsInput: GdRetargetingsContainerInput!
        $goalsInput: GdGoalsContainerInput!
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
                countryRegionId
            }
            retargetings(input: $retargetingsInput) {
                rowset {
                    ...retargetingsRowFragment
                }
                totalCount
                totalStats {
                    shows
                    clicks
                    ctr
                    cost
                }
                hasObjectsOverLimit
                retargetingIds
                cacheKey
            }
            goals(input: $goalsInput) {
                rowset {
                    id
                    domain
                    name
                    counterId
                    time
                }
            }
        }
    }
    ${retargetingsRowFragment}
    ${clientFeaturesFragment}
`;

export const usersProfileQuery = gql`
    query UsersProfileQuery(
        $login: String!
        $retargetingsInput: GdRetargetingsContainerInput!
        $goalsInput: GdGoalsContainerInput!
    ) {
        reqId: getReqId
        cryptaSegments(input: { filter: { name: "" } }) {
            totalCount
            rowset {
                id
                type
                name
                parentId
                description
                interestType
                canSelectAll
                mutuallyExclusiveIds
            }
        }
        client(searchBy: { login: $login }) {
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
                countryRegionId
            }
            retargetings(input: $retargetingsInput) {
                rowset {
                    ...retargetingsRowFragment
                }
                totalStats {
                    shows
                    clicks
                    ctr
                    cost
                }
                totalCount
                hasObjectsOverLimit
                retargetingIds
                cacheKey
            }
            goals(input: $goalsInput) {
                rowset {
                    id
                    domain
                    type
                    name
                    counterId
                    time
                }
            }
        }
    }
    ${clientFeaturesFragment}
    ${retargetingsRowFragment}
`;

export const adsTouchQuery = gql`
    query BannersTouchQuery($login: String!, $adsInput: GdAdsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            ads(input: $adsInput) {
                rowset {
                    ...adsRowFragment
                }
                totalCount
                adIds
                cacheKey
                totalStats {
                    shows
                    clicks
                    ctr
                    cost
                }
                hasObjectsOverLimit
            }
        }
    }
    ${adsRowFragment}
`;

export const adsQuery = gql`
    query BannersQuery($login: String!, $adsInput: GdAdsContainerInput!, $placementsInput: GdPlacementFilterInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
                countryRegionId
            }
            ads(input: $adsInput) {
                rowset {
                    ...adsRowFragment
                }
                totalCount
                adIds
                cacheKey
                totalStats {
                    shows
                    clicks
                    ctr
                    cost
                }
                hasObjectsOverLimit
            }
        }
        operator {
            features {
                isBannerUpdateAllowed
                isCpmBannerUpdateAllowed
            }
        }
        constants {
            configuration {
                canvasUiDomain
            }
        }
        placements(input: $placementsInput) {
            placements {
                operatorName
                id
            }
        }
    }
    ${adsRowFragment}
    ${statsDataFragment}
    ${clientFeaturesFragment}
`;

export const adsPreviewRowFragment = gql`
    fragment adsPreviewRowFragment on GdAd {
        id
        title
        titleExtension
        body
        domain
        dynamicDisclaimer
        linkTail
        type
        href
        access {
            canEdit
        }
        status {
            primaryStatus
            previousVersionShown
            rejectedOnModeration
        }
        adGroup {
            ...AdGroupTruncatedFragment
        }
        image {
            ...adImageFragment
        }
        bannerImage {
            ...adImageFragment
        }
        typedCreative {
            previewUrl
            height
            width
            name
            ... on GdCanvasCreative {
                isAdaptive
            }
        }
        sitelinks {
            href
        }
        flags
        ... on GdContentPromotionAd {
            contentPromotionUrl
            contentPromotionType
            contentPromotionVisitUrl
            contentPromotionPreviewUrl
        }
    }
    ${bannerImageFragment}
    ${adGroupTruncatedFragment}
`;

export const adConstantsFragment = gql`
    fragment adConstantsFragment on GdDirectConstantsAdValidationData {
        maxLengthTitle
        maxLengthTitleWord
        maxLengthTitleExtension
        maxLengthBody
        maxLengthBodyWord
        narrowSymbols
        maxNumberOfNarrowCharacters
        allowAdLetters
        maxLengthHref
        maxLengthTurboGalleryHref
        maxLengthDisplayHref
        allowAdDisplayHrefLetters
        aggregationDomains
    }
`;

export const calloutsConstantsFragment = gql`
    fragment calloutsConstantsFragment on GdDirectConstantsCalloutValidationData {
        calloutLettersAllowed
        calloutTextMaxLength
        calloutsOnClientMaxCount
        calloutsOnClientMaxCountWithDeleted
    }
`;

export const sitelinkConstantsFragment = gql`
    fragment sitelinkConstantsFragment on GdDirectConstantsSitelinkValidationData {
        allowLetters
        maxDescriptionLength
        maxHrefLength
        maxTitleLength
        sitelinkMaxCount
    }
`;

export const adsPreviewQuery = gql`
    query BannersPreviewQuery($login: String!, $adsInput: GdAdsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            ads(input: $adsInput) {
                rowset {
                    ...adsPreviewRowFragment
                }
                totalCount
            }
        }
    }
    ${adsPreviewRowFragment}
`;

export const groupAdQuery = gql`
    query GroupAdQuery($login: String!, $adsInput: GdAdsContainerInput!, $calloutsFilter: GdCalloutFilterInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            ads(input: $adsInput) {
                rowset {
                    id
                }
            }
        }
        callouts(input: { searchBy: { login: $login }, filter: $calloutsFilter }) {
            id
        }
    }
`;

export const groupBannersQuery = gql`
    query GroupBannersQuery($login: String!, $adsInput: GdAdsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            ads(input: $adsInput) {
                rowset {
                    creative {
                        height
                        width
                    }
                    typedCreative {
                        creativeType
                        moderationInfo {
                            aspects {
                                height
                                width
                            }
                        }
                        ... on GdCanvasCreative {
                            isAdaptive
                        }
                    }
                }
            }
        }
    }
`;

export const calloutsIdsQuery = gql`
    query CalloutsIdsQuery($login: String!, $calloutsFilter: GdCalloutFilterInput!) {
        reqId: getReqId
        callouts(input: { searchBy: { login: $login }, filter: $calloutsFilter }) {
            id
        }
    }
`;

export const turbolandnigQuery = gql`
    query TurbolandingQuery($login: String!, $filter: GdTurbolandingFilterInput!) {
        reqId: getReqId
        turbolandings(input: { searchBy: { login: $login }, filter: $filter }) {
            id
            clientId
            name
            href
        }
    }
`;

export const calloutFragment = gql`
    fragment calloutFragment on GdCallout {
        clientId
        id
        flags
        text
        statusModerate
    }
`;

export const imageFragment = gql`
    fragment imageFragment on GdImage {
        avatarsHost
        createTime
        imageHash
        mdsGroupId
        name
        namespace
        type
        formats {
            ...adImageFormatsFragment
        }
        imageSize {
            width
            height
        }
    }
    ${bannerImageFormatsFragment}
`;

export const adsRowFragmentForEdit = gql`
    fragment adsRowFragmentForEdit on GdAd {
        id
        campaignId
        adGroupId
        exportId
        type
        title
        titleExtension
        adGroup {
            ...AdGroupTruncatedFragment
        }
        body
        access {
            canEdit
            canBeDeleted
        }
        domain
        href
        linkTail
        vcardId
        isMobile
        dynamicDisclaimer
        reverseDomain
        optsGeoFlag
        optsNoDisplayHref
        phoneFlag
        dynamicDisclaimer
        flags
        image {
            ...adImageFragment
        }
        bannerImage {
            ...adImageFragment
        }
        callouts {
            ...calloutFragment
        }
        typedCreative {
            creativeId
            creativeType
            height
            name
            width
            previewUrl
            ... on GdVideoAdditionCreative {
                livePreviewUrl
            }
            ... on GdCpmAudioCreative {
                livePreviewUrl
                duration
            }

            ... on GdHtml5Creative {
                isImage
            }

            ... on GdCpmOutdoorCreative {
                livePreviewUrl
                duration
                additionalData {
                    formats {
                        height
                        width
                    }
                    duration
                }
            }

            ... on GdCpmIndoorCreative {
                livePreviewUrl
            }

            ... on GdSmartCreative {
                layout {
                    name
                }
                groupName
                groupId
            }
            ... on GdCpcVideoCreative {
                livePreviewUrl
            }
            ... on GdCpmVideoCreative {
                livePreviewUrl
            }
            ... on GdCanvasCreative {
                isAdaptive
            }
        }
        status {
            primaryStatus
            previousVersionShown
            rejectedOnModeration
        }
        sitelinksSetId
        sitelinks {
            description
            href
            title
            turbolanding {
                href
                id
                name
            }
        }
        turbolanding {
            href
            hrefParams
            id
            name
            statusModerate
        }
        ... on GdTextAd {
            turboGalleryHref
            bannerPrice {
                currency
                prefix
                price
                priceOld
            }
            permalinkId
            permalinkAssignType
        }
        ... on GdCPMBannerAd {
            pixels {
                kind
                url
            }
            measurers {
                measurerSystem
                params
                hasIntegration
            }
            tnsId
        }
        ... on GdContentPromotionAd {
            contentPromotionPreviewUrl
            contentPromotionVisitUrl
            contentPromotionUrl
            contentPromotionId
        }
        ... on GdCPMOutdoorBannerAd {
            pixels {
                kind
                url
            }
            placementPages {
                bannerId
                comment
                createTime
                id
                pageId
                statusModerate
                statusModerateOperator
                version
            }
        }
        ... on GdCPMAudioBannerAd {
            pixels {
                kind
                url
            }
            measurers {
                measurerSystem
                params
                hasIntegration
            }
        }
    }
    ${calloutFragment}
    ${bannerImageFragment}
    ${adGroupTruncatedFragment}
`;

export const configurationFragment = gql`
    fragment configurationFragment on GdConfigurationConstants {
        turbolandingUnifiedApiUrl
        canvasUiDomain
    }
`;

export const imagesQueryForEdit = gql`
    query ImagesQueryForEdit(
        $login: String!
        $cacheKey: String!
        $filter: GdImageFilterInput!
        $limitOffset: GdLimitOffsetInput!
    ) {
        reqId: getReqId
        images(
            input: { searchBy: { login: $login }, cacheKey: $cacheKey, limitOffset: $limitOffset, filter: $filter }
        ) {
            cacheKey
            imageHashes
            rowset {
                ...imageFragment
            }
            totalCount
        }
    }
    ${imageFragment}
`;

export const adsQueryForEdit = gql`
    query BannersQueryForEdit(
        $login: String!
        $adsInput: GdAdsContainerInput!
        $campaignsInput: GdCampaignsContainerInput!
        $placementsInput: GdPlacementFilterInput!
    ) {
        callouts(input: { searchBy: { login: $login }, filter: { deleted: false } }) {
            ...calloutFragment
        }

        reqId: getReqId
        client(searchBy: { login: $login }) {
            measurerAccounts
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
            }
            ads(input: $adsInput) {
                rowset {
                    ...adsRowFragmentForEdit
                }
                totalCount
                adIds
                hasObjectsOverLimit
            }
            campaigns(input: $campaignsInput) {
                rowset {
                    id
                    promocodeRestrictedDomain
                    ... on GdTextCampaign {
                        defaultPermalinkId
                    }
                }
            }
        }
        operator {
            features {
                isBannerUpdateAllowed
                isCpmBannerUpdateAllowed
            }
            access {
                canEditAdDomain
            }
        }
        constants {
            validation {
                adConstants {
                    ...adConstantsFragment
                }
                vcardConstants {
                    ...vcardConstantsFragment
                }
                calloutConstants {
                    ...calloutsConstantsFragment
                }
                sitelinkConstants {
                    ...sitelinkConstantsFragment
                }
            }
            configuration {
                ...configurationFragment
            }
        }
        placements(input: $placementsInput) {
            placements {
                operatorName
                id
            }
        }
    }
    ${configurationFragment}
    ${calloutFragment}
    ${adsRowFragmentForEdit}
    ${clientFeaturesFragment}
    ${adConstantsFragment}
    ${vcardConstantsFragment}
    ${calloutsConstantsFragment}
    ${sitelinkConstantsFragment}
`;

export const baseInfoForBannerCreationQuery = gql`
    query BaseInfoForBannerCreationQuery(
        $login: String!
        $campaignInput: GdCampaignsContainerInput!
        $adGroupInput: GdAdGroupsContainerInput!
    ) {
        callouts(input: { searchBy: { login: $login }, filter: { deleted: false } }) {
            ...calloutFragment
        }

        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            features {
                ...ClientFeaturesFragment
            }
            info {
                id
                workCurrency
            }
            campaigns(input: $campaignInput) {
                rowset {
                    strategy {
                        platform
                        ... on GdStrategyOptimizeConversions {
                            payForConversion
                        }
                    }
                    name
                    id
                    type
                    access {
                        canEdit
                    }
                    hasAds
                    status {
                        primaryStatus
                    }
                    promocodeRestrictedDomain
                    ... on GdTextCampaign {
                        lastChangedVcardId
                        commonVcard {
                            ...VCardCampaignDataFragment
                        }
                        defaultPermalinkId
                    }
                    ... on GdDynamicCampaign {
                        lastChangedVcardId
                        commonVcard {
                            ...VCardCampaignDataFragment
                        }
                    }
                }
            }
            adGroups(input: $adGroupInput) {
                rowset {
                    id
                    adsCount
                    name
                    type
                    access {
                        canEdit
                    }
                    status {
                        primaryStatus
                    }
                }
            }
        }
        operator {
            features {
                isBannerUpdateAllowed
                isCpmBannerUpdateAllowed
            }
            access {
                canEditAdDomain
            }
        }
        constants {
            validation {
                adConstants {
                    ...adConstantsFragment
                }
                vcardConstants {
                    ...vcardConstantsFragment
                }
                calloutConstants {
                    ...calloutsConstantsFragment
                }
                sitelinkConstants {
                    ...sitelinkConstantsFragment
                }
            }
            configuration {
                ...configurationFragment
            }
        }
    }
    ${configurationFragment}
    ${calloutFragment}
    ${clientFeaturesFragment}
    ${adConstantsFragment}
    ${vCardCampaignDataFragment}
    ${vcardConstantsFragment}
    ${calloutsConstantsFragment}
    ${sitelinkConstantsFragment}
`;

export const adsInternalRowFragmentForEdit = gql`
    fragment adsInternalRowFragmentForEdit on GdInternalAd {
        id
        campaignId
        adGroupId
        type
        description
        templateId
        templateVars {
            templateResourceId
            value
        }
        access {
            canEdit
            canBeDeleted
        }
        adGroup {
            ...AdGroupInternalTruncatedFragment
        }
        images {
            image {
                ...adImageFragment
            }
            templateResourceId
        }
        status {
            primaryStatus
            previousVersionShown
            rejectedOnModeration
        }
    }
    ${adGroupInternalTruncatedFragment}
    ${bannerImageFragment}
`;

export const adsInternalQueryForEdit = gql`
    query InternalBannersQueryForEdit($login: String!, $adsInput: GdAdsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            info {
                id
            }
            ads(input: $adsInput) {
                rowset {
                    ...adsInternalRowFragmentForEdit
                }
                totalCount
                adIds
                hasObjectsOverLimit
            }
        }
    }
    ${adsInternalRowFragmentForEdit}
`;

export const adsInternalQueryForCreation = gql`
    query InternalBannersQueryForCreation($login: String!, $adGroupsInput: GdAdGroupsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            info {
                id
            }
            adGroups(input: $adGroupsInput) {
                rowset {
                    ...AdGroupInternalTruncatedFragment
                }
            }
        }
    }
    ${adGroupInternalTruncatedFragment}
`;

export const internalTemplateResourcesQuery = gql`
    query InternalTemplateResourcesQuery($login: String!, $entries: [GdInternalTemplateInfoRequestEntryInput!]!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            internalAdTemplateInfo(request: { entries: $entries }) {
                campaignId
                templateId
                resources {
                    id
                    label
                    type
                    choices {
                        display
                        value
                    }
                    valueRestrictions {
                        type
                        strict
                        defectCode
                        ... on GdUrlCorrectRestriction {
                            isHttpsOnly
                        }
                        ... on GdLengthValueRestriction {
                            length
                        }
                        ... on GdImageFormatValueRestriction {
                            format
                        }
                        ... on GdImageSizeKbMaxValueRestriction {
                            size
                        }
                        ... on GdImageDimensionsValueRestriction {
                            width
                            height
                        }
                    }
                }
                resourceRestrictions {
                    required
                    absent
                }
            }
        }
    }
`;

export const internalTemplatePlacesQuery = gql`
    query InternalTemplatePlacesQuery($placesIds: [Long!]) {
        reqId: getReqId
        constants {
            internalTemplatePlaces(input: { filter: { placesIds: $placesIds } }) {
                rowset {
                    placeId
                    templateId
                }
            }
        }
    }
`;

export const pointFragmet = gql`
    fragment PointFragmet on GdPointOnMap {
        x
        y
        x1
        y1
        x2
        y2
    }
`;

export const vCardDataFragment = gql`
    fragment VCardDataFragment on GdVcard {
        id
        campaignId
        companyName
        contactPerson
        extraMessage
        email
        ogrn

        instantMessenger {
            type
            login
        }

        workTimes {
            daysOfWeek
            startTime {
                hour
                minute
            }
            endTime {
                hour
                minute
            }
        }

        phone {
            countryCode
            cityCode
            phoneNumber
            extension
        }

        address {
            geoId
            country
            city
            street
            houseWithBuilding
            apartment
            metroStationId
            metroStationName
            pointPrecision
            pointOnMap {
                ...PointFragmet
            }
        }
    }
    ${pointFragmet}
`;

export const vCardsQuery = gql`
    query VCardsForBannersEdit($login: String!, $vcardIdIn: [Long!]) {
        reqId: getReqId
        vcards(input: { filter: { vcardIdIn: $vcardIdIn }, searchBy: { login: $login } }) {
            rowset {
                ...VCardDataFragment
            }
        }
    }
    ${vCardDataFragment}
`;

export const metroStationsFragment = gql`
    fragment MetroStationsFragment on GdMetroStationsData {
        rowset {
            geoRegionId
            metroStationId
            metroStationName
        }
    }
`;

export const metroStationsQuery = gql`
    query MetroStationsQuery($city: [GdMetroCityInput!]) {
        reqId: getReqId
        constants {
            metroStations(input: { filter: { metroCityIn: $city } }) {
                ...MetroStationsFragment
            }
        }
    }
    ${metroStationsFragment}
`;

export const getMetroByCoordinatesQuery = gql`
    query GetMetroByCoordinates($coordinates: GdCoordinatesInput!) {
        reqId: getReqId
        getMetroByCoordinates(coordinates: $coordinates) {
            geoId
        }
    }
`;

export const getGeoByAddressQuery = gql`
    query GetGeoByAddress($address: GdGeoToolsAddressInput!) {
        reqId: getReqId
        getGeoByAddress(address: $address) {
            geoId
        }
    }
`;

export const getLastChangedAdsQuery = gql`
    query GetLastChangedAds(
        $login: String!
        $lastChangedAdsInput: GdLastChangedAdContainerInput!
        $placementsInput: GdPlacementFilterInput!
    ) {
        client(searchBy: { login: $login }) {
            info {
                id
                workCurrency
            }
            lastChangedAds(input: $lastChangedAdsInput) {
                lastChangedAds {
                    ad {
                        ...adsRowFragmentForEdit
                    }
                }
            }
            features {
                ...ClientFeaturesFragment
            }
        }

        operator {
            features {
                isBannerUpdateAllowed
                isCpmBannerUpdateAllowed
            }
        }

        placements(input: $placementsInput) {
            placements {
                operatorName
                id
            }
        }

        reqId: getReqId
    }
    ${adsRowFragmentForEdit}
    ${clientFeaturesFragment}
`;

export const getGeoByCoordinatesQuery = gql`
    query GetGeoByCoordinates($coordinates: GdCoordinatesInput!) {
        reqId: getReqId
        getGeoByCoordinates(coordinates: $coordinates) {
            geoId
        }
    }
`;

export const validatePrice = gql`
    query validateAdPrice($bannerPrice: [GdAdPriceInput]!) {
        reqId: getReqId
        validateAdPrice(input: $bannerPrice) {
            errors {
                code
                path
                params
            }
        }
    }
`;

export const adGroupsRowFragmentForEdit = gql`
    fragment adGroupsRowFragmentForEdit on GdAdGroup {
        access {
            showGeneralPriceOnEdit
            canEdit
        }
        regionsInfo {
            regionIds
        }
        mainAd {
            ...adPreviewFragment
        }
        campaign {
            id
            name
            strategy {
                platform
                strategyType
                isAutoBudget
            }
            type
            ...PriceCampaignDataForGroupFragment
        }
        id
        name
        type
        adsCount
        availableShowConditionTypes
        productRestrictionId
        pageGroupTags
        targetTags
        libraryMinusKeywordsPacks {
            id
            name
        }
        minusKeywords
        ... on GdSmartAdGroup {
            feedId
            fieldToUseAsBody
            fieldToUseAsName
            trackingParams
        }
        bidModifiers {
            id
            enabled
            type
            adjustments {
                ...commonBidModifiersAdjustmentFragment
            }
        }
        ... on GdInternalAdGroup {
            level
            targetingInternalNetwork {
                targetingMode
                joinType
            }
            targetingIsBrowser {
                targetingMode
                joinType
            }
            targetingIsTablet {
                targetingMode
                joinType
            }
            targetingIsMobile {
                targetingMode
                joinType
            }
            targetingIsTouch {
                targetingMode
                joinType
            }
            targetingUserAgents {
                targetingMode
                joinType
                value
            }
            targetingOsNames {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                }
            }
            targetingOsFamilies {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                    minVersion
                    maxVersion
                }
            }
            targetingBrowserNames {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                    minVersion
                    maxVersion
                }
            }
            targetingBrowserEngines {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                    minVersion
                    maxVersion
                }
            }
            targetingDeviceVendors {
                targetingMode
                joinType
                value {
                    targetingValueEntryId
                }
            }
            targetingDeviceNames {
                targetingMode
                joinType
                value
            }
            targetingInterfaceLangs {
                targetingMode
                joinType
                value
            }
            targetingQueryReferers {
                targetingMode
                joinType
                value
            }
            targetingYandexUids {
                targetingMode
                joinType
                value
            }
            targetingShowDates {
                targetingMode
                joinType
                value
            }
            targetingClidTypes {
                targetingMode
                joinType
                value
            }
            targetingClids {
                targetingMode
                joinType
                value
            }
            targetingDesktopInstalledApps {
                targetingMode
                joinType
                value
            }
            targetingMobileInstalledApps {
                targetingMode
                joinType
                value
            }
            targetingQueryOptions {
                targetingMode
                joinType
                value
            }
            targetingTestIds {
                targetingMode
                joinType
                value
            }
            targetingYsCookies {
                targetingMode
                joinType
                value
            }
            targetingIsYandexPlus {
                targetingMode
                joinType
            }
            targetingIsVirused {
                targetingMode
                joinType
            }
            startTime
            finishTime
        }
        ... on GdCpmVideoAdGroup {
            videoGoals
        }
        ... on GdCpmAudioAdGroup {
            videoGoals
        }
        ... on GdCpmOutdoorAdGroup {
            videoGoals
            pageBlocks {
                impId
                pageId
            }
        }
        ... on GdCpmIndoorAdGroup {
            videoGoals
            pageBlocks {
                impId
                pageId
            }
        }
        ... on GdCpmPriceAdGroup {
            priority
        }
    }
    ${adPreviewFragment}
    ${commonBidModifiersAdjustmentFragment}
    ${priceCampaignDataForGroupFragment}
`;

export const feedsFragment = gql`
    fragment feedsFragment on GdFeedsContext {
        rowset {
            id
            name
            updateStatus
            businessType
            categories {
                categoryId
                offerCount
                parentCategoryId
                name
                isDeleted
            }
            feedType
        }
    }
`;

export const adGroupsQueryForEdit = gql`
    query GroupsQueryForEdit(
        $login: String!
        $adGroupsInput: GdAdGroupsContainerInput!
        $showConditionsInput: GdShowConditionsContainerInput!
        $smartFiltersInput: GdSmartFiltersContainerInput!
        $retargetingsInput: GdRetargetingsContainerInput!
        $interestsInput: GdRetargetingsContainerInput!
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin

            clientConstants {
                allowedBidModifierTypesByAdGroupTypes {
                    adGroupType
                    allowedBidModifierTypes
                }
                allowedBidModifiersValues {
                    ...allowedBidModifiersValuesFragment
                }
                clientLimits {
                    keywordsCountLimit
                }
            }

            adGroups(input: $adGroupsInput) {
                rowset {
                    ...adGroupsRowFragmentForEdit
                }
                totalCount
                adGroupIds
            }

            retargetings(input: $retargetingsInput) {
                rowset {
                    adGroupId
                    retargetingConditionId
                    retargetingId
                    priceContext
                }
            }
            interests: retargetings(input: $interestsInput) {
                rowset {
                    adGroupId
                    retargetingConditionId
                    retargetingId
                    priceContext
                }
            }
            showConditions(input: $showConditionsInput) {
                rowset {
                    ... on GdKeyword {
                        id
                        keyword
                        minusKeywords
                        adGroupId
                    }

                    ... on GdRelevanceMatch {
                        id
                        adGroupId
                    }
                }
            }

            smartFilters(input: $smartFiltersInput) {
                rowset {
                    id
                    adGroupId
                    name
                    autobudgetPriority
                    priceCpa
                    priceCpc
                    targetFunnel
                    tab
                    autobudgetPriority
                    conditions {
                        field
                        operator
                        stringValue
                    }
                }
            }

            feeds {
                ...feedsFragment
            }
        }
    }
    ${allowedBidModifiersValuesFragment}
    ${adGroupsRowFragmentForEdit}
    ${feedsFragment}
`;

export const adGroupsQueryPageBlocks = gql`
    query GroupsQueryPageBlocks($adGroupsInput: GdAdGroupsContainerInput!, $login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            adGroups(input: $adGroupsInput) {
                rowset {
                    ... on GdCpmIndoorAdGroup {
                        pageBlocks {
                            pageId
                            blockId: impId
                        }
                    }
                    ... on GdCpmOutdoorAdGroup {
                        pageBlocks {
                            pageId
                            blockId: impId
                        }
                    }
                }
            }
        }
    }
`;

export const adGroupsQueryKeywordsAndRetargeting = gql`
    query GroupsQueryKeywordsAndRetargeting(
        $login: String!
        $showConditionsInput: GdShowConditionsContainerInput!
        $retargetingsInput: GdRetargetingsContainerInput!
        $interestsInput: GdRetargetingsContainerInput!
    ) {
        reqId: getReqId
        cryptaSegments(input: { filter: { name: "" } }) {
            totalCount
            rowset {
                id
                type
                name
                parentId
                description
                interestType
                canSelectAll
                mutuallyExclusiveIds
            }
        }
        client(searchBy: { login: $login }) {
            retargetings(input: $retargetingsInput) {
                rowset {
                    retargetingCondition {
                        name
                    }
                }
            }

            interests: retargetings(input: $interestsInput) {
                rowset {
                    retargetingId
                    retargetingCondition {
                        name
                        description
                        conditionRules {
                            type
                            interestType
                            goals {
                                id
                                type
                                time
                            }
                        }
                    }
                }
            }

            showConditions(input: $showConditionsInput) {
                rowset {
                    id
                    ... on GdKeyword {
                        keyword
                    }
                }
            }
        }
    }
`;

export const productsListQuery = gql`
    query ProductsListQuery($productFilterInput: GdProductContainerInput!) {
        reqId: getReqId
        products(input: $productFilterInput) {
            totalCount
            rowset {
                id
                type
                restrictions {
                    id
                    groupType
                    active
                    conditions {
                        name
                        values
                    }
                }
            }
        }
    }
`;

export const smartFiltersQuery = gql`
    query SmartFiltersQuery($login: String!, $smartFiltersInput: GdSmartFiltersContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            smartFilters(input: $smartFiltersInput) {
                rowset {
                    id
                    adGroupId
                    name
                    autobudgetPriority
                    priceCpa
                    priceCpc
                    targetFunnel
                    tab
                    autobudgetPriority
                    conditions {
                        field
                        operator
                        stringValue
                    }
                }
            }
        }
    }
`;

export const priceCampaignBasicDataFragment = gql`
    fragment PriceCampaignBasicDataFragment on GdPriceCampaign {
        pricePackageId
        startDate
        endDate
        flightOrderVolume
        hasDefaultAdGroup
        flightTargetingsSnapshot {
            behaviors
            viewTypes
            geoExpanded
        }
        pricePackage {
            id
            title
            targetingsFixed {
                geo
            }
            targetingsCustom {
                geo
            }
        }
    }
`;

export const campaignBasicDataForGroupEdit = gql`
    query CampaignBasicDataForGroupEdit($login: String!, $campInput: GdCampaignsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            campaigns(input: $campInput) {
                rowset {
                    id
                    type
                    name
                    defaultRegionIds
                    strategy {
                        strategyType
                        isAutoBudget
                    }
                    availableAdGroupTypes
                    ...PriceCampaignBasicDataFragment
                }
            }

            clientConstants {
                allowedBidModifierTypesByAdGroupTypes {
                    adGroupType
                    allowedBidModifierTypes
                }
                allowedBidModifiersValues {
                    ...allowedBidModifiersValuesFragment
                }
                clientLimits {
                    keywordsCountLimit
                }
            }
        }
    }
    ${allowedBidModifiersValuesFragment}
    ${priceCampaignBasicDataFragment}
`;

export const regionTreeForGroupEdit = gql`
    query RegionTreeForGroupEdit($login: String!, $campInput: GdCampaignsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            campaigns(input: $campInput) {
                rowset {
                    id
                    type
                    ... on GdPriceCampaign {
                        customGeoExpanded {
                            geoExpandedTrees {
                                id
                                name
                                inner {
                                    id
                                    name
                                    inner {
                                        id
                                        name
                                        inner {
                                            id
                                            name
                                            inner {
                                                id
                                                name
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const creativeFragment = gql`
    fragment creativeFragment on GdTypedCreative {
        creativeId
        creativeType
        height
        name
        previewUrl
        width

        ... on GdCanvasCreative {
            isAdaptive
        }

        ... on GdHtml5Creative {
            isImage
        }
    }
`;

export const creativesQuery = gql`
    query CreativesQuery($input: GdCreativesContainerInput!) {
        reqId: getReqId
        creatives(input: $input) {
            cacheKey
            creativeIds
            rowset {
                ...creativeFragment
            }
            totalCount
        }
    }
    ${creativeFragment}
`;

export const smartFeedSchemaQuery = gql`
    query SmartFeedSchemaQuery($input: GdSmartFilterSchemaQueryInput) {
        reqId: getReqId
        constants {
            smartFilterSchemas(query: $input) {
                fields {
                    name
                    operators {
                        maxItems
                        type
                    }

                    ... on GdSmartFilterNumberField {
                        max
                        min
                        precision
                    }

                    ... on GdSmartFilterStringField {
                        maxLength
                        minLength
                        pattern
                    }

                    ... on GdSmartFilterEnumField {
                        values
                    }
                }
            }
        }
    }
`;

export const performanceCreativesQuery = gql`
    query PerformanceCreativesQuery(
        $login: String!
        $adsInput: GdAdsContainerInput!
        $adGroupInput: GdAdGroupsContainerInput!
        $limit: Int!
        $offset: Int!
        $order: OrderInput!
        $idOrName: String!
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            ads(input: $adsInput) {
                rowset {
                    ... on GdSmartAd {
                        creative {
                            creativeId
                        }
                    }
                }
            }
            adGroups(input: $adGroupInput) {
                rowset {
                    ... on GdSmartAdGroup {
                        availableCreatives(
                            input: {
                                limitOffset: { limit: $limit, offset: $offset }
                                idSortOrder: $order
                                filter: { idOrNameLike: $idOrName }
                            }
                        ) {
                            rowset {
                                groupId
                                groupName
                                creativeId
                                businessType
                                statusModerate
                                groupName
                                groupId
                                businessType
                                statusModerate
                                previewUrl
                                height
                                width
                                name
                                layout {
                                    id
                                    imgSrc
                                    name
                                }
                                theme {
                                    id
                                    name
                                }
                                usedInCampaigns {
                                    id
                                    name
                                }
                                regionIds
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const feedsQuery = gql`
    query FeedsQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            feeds {
                ...feedsFragment
            }
        }
    }
    ${feedsFragment}
`;

export const smartFiltersGridQuery = gql`
    query SmartFiltersGridQuery($login: String!, $smartFiltersInput: GdSmartFiltersContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            chiefLogin
            smartFilters(input: $smartFiltersInput) {
                rowset {
                    id
                    adGroup {
                        ...AdGroupTruncatedFragment
                    }
                    name
                    autobudgetPriority
                    priceCpa
                    priceCpc
                    targetFunnel
                    tab
                    autobudgetPriority
                    conditions {
                        field
                        operator
                        stringValue
                    }
                    stats {
                        avgClickCost
                        goals
                        cost
                    }
                    status {
                        primaryStatus
                    }
                }
                cacheKey
                smartFilterIds
                totalCount
                totalStats {
                    shows
                    ctr
                    cost
                    clicks
                }
            }
        }
    }
    ${adGroupTruncatedFragment}
`;

export const copyAdGroupsInfo = gql`
    query CopyAdGroupsInfoQuery($login: String!, $adGroupInput: GdAdGroupsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            adGroups(input: $adGroupInput) {
                rowset {
                    name
                    id
                    campaign {
                        type
                        name
                        id
                    }
                }
            }
        }
    }
`;

export const goalsQuery = gql`
    query GoalsQuery($login: String!, $goalsInput: GdCampaignGoalsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            campaignGoals(input: $goalsInput) {
                rowset {
                    id
                    name
                }
            }
        }
    }
`;

export const groupKeywordsQuery = gql`
    query GroupKeywordsQuery($login: String!, $input: GdShowConditionsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            showConditions(input: $input) {
                rowset {
                    id
                    ... on GdKeyword {
                        keyword
                    }
                }
            }
        }
    }
`;

export const reachQuery = gql`
    query ReachQuery($input: GdReachRequestInput!) {
        reqId: getReqId
        reach(input: $input) {
            basic {
                reach
                reachLessThan
            }
            detailed {
                reach
                reachLessThan
            }
            requestId
        }
    }
`;

export const reachRecommendationQuery = gql`
    query ReachRecommendationQuery($input: GdReachRequestInput!) {
        reqId: getReqId
        reachRecommendation(input: $input) {
            bannerFormats {
                format
                increasePercent
            }
            requestId
        }
    }
`;

export const recommendationsAdsGroupEditQuery = gql`
    query RecommendationsAdsGroupEditQuery($login: String!, $adGroupInput: GdAdGroupsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            adGroups(input: $adGroupInput) {
                rowset {
                    id
                    type
                    name
                    recommendations {
                        ...recommendationFragment
                    }
                }
            }
        }
    }
    ${recommendationFragment}
`;

export const recommendationsGroupEditQuery = gql`
    query RecommendationsGroupEditQuery($login: String!, $adGroupInput: GdAdGroupsContainerInput!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            adGroups(input: $adGroupInput) {
                rowset {
                    id
                    type
                    name
                    recommendations {
                        ...recommendationFragment
                    }
                }
            }
        }
    }
    ${recommendationFragment}
`;

export const reachOutdoorQuery = gql`
    query ReachOutdoorQuery($input: GdOutdoorReachRequestInput!) {
        reqId: getReqId
        reachOutdoor(input: $input) {
            otsCapacity
            reach
            reachLessThan
            requestId
        }
    }
`;

export const reachIndoorQuery = gql`
    query ReachIndoorQuery($input: GdIndoorReachRequestInput!) {
        reqId: getReqId
        reachIndoor(input: $input) {
            otsCapacity
            reach
            reachLessThan
            requestId
        }
    }
`;

export const topRegionQuery = gql`
    query TopRegionQuery($input: GdClientTopRegionsInput!) {
        reqId: getReqId
        adGroupTopRegions(input: $input) {
            regions {
                count
                regionIds
            }
        }
    }
`;

export const getContentPromotionMetadata = gql`
    query GetContentPromotionMetadata($input: GdGetContentPromotionMetaInputInput!) {
        reqId: getReqId
        getContentPromotionMeta(input: $input) {
            contentPromotionMeta {
                contentId
                title
                description
                url
                previewUrl
            }
            validationResult {
                errors {
                    ...ValidationErrorFragment
                }
                warnings {
                    ...ValidationWarningFragment
                }
            }
        }
    }
    ${ValidationErrorFragment}
    ${ValidationWarningFragment}
`;
